import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { BookmarkApiEndpoints } from '../../../shared/enum/api-endpoints.enum';
import { embedImagesInHtml, normalizeImageUrls, splitExplanationImages, stripEmbeddedImages } from '../../../shared/utils/question-html.util';
import {
  BookmarkApiItem,
  BookmarkDetailData,
  BookmarkDetailResponse,
  BookmarkPageRequest,
  BookmarkPageResponse,
  CreateBookmarkPayload,
  CreateBookmarkResponse,
  RemoveBookmarkResponse,
  TrackEventPayload,
  TrackEventResponse,
} from '../model/test-series.model';

@Injectable({
  providedIn: 'root',
})
export class BookmarkService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  getBookmarkedQuestions(payload: BookmarkPageRequest): Observable<BookmarkPageResponse> {
    return this.http.post<BookmarkPageResponse>(
      `${this.apiUrl}/${BookmarkApiEndpoints.GET_BOOKMARKS}`,
      payload,
    );
  }

  getBookmarkById(bookmarkId: string): Observable<BookmarkDetailResponse> {
    return this.http.get<BookmarkDetailResponse>(
      `${this.apiUrl}/${BookmarkApiEndpoints.REMOVE_BOOK_MARK}/${bookmarkId}`,
    );
  }

  mapBookmarkItem(item: BookmarkApiItem) {
    return {
      id: item.bookmarkId,
      bookmarkId: item.bookmarkId,
      examId: item.examId,
      questionId: item.questionId,
      serialNumber: item.serialNumber,
      question: stripEmbeddedImages(item.question),
      explanation: item.explanation,
      subject: item.subject,
      chapter: item.chapter,
      topic: item.topic,
      difficulty_level: item.difficultyLevel,
      correct_answer: item.correctAnswer,
      is_Bookmarked: item.bookmarked,
      options: [
        { key: 'A', text: item.optionA },
        { key: 'B', text: item.optionB },
        { key: 'C', text: item.optionC },
        { key: 'D', text: item.optionD },
      ],
    };
  }

  mapBookmarkDetail(data: BookmarkDetailData) {
    // Question images go into their <img> placeholders in the HTML (e.g. a table cell);
    // only URLs without a placeholder are rendered below the content. Explanation HTML
    // keeps its own <img> src; its image URL array is rendered below it.
    const question = embedImagesInHtml(
      data.questionText,
      normalizeImageUrls(data.questionImage ?? data.questionImageUrl ?? data.question_image_url),
    );
    const explanation = splitExplanationImages(
      data.explanation,
      normalizeImageUrls(data.explanationImage ?? data.explanationImageUrl ?? data.explanation_image_url),
    );

    return {
      id: data.bookmarkId,
      bookmarkId: data.bookmarkId,
      examId: data.examId,
      questionId: data.questionId,
      serialNumber: data.questionNumber,
      question: question.html,
      explanation: explanation.html,
      subject: data.subject ?? '',
      chapter: data.chapter ?? '',
      topic: data.topic ?? '',
      difficulty_level: data.difficultyLevel ?? '',
      correct_answer: data.correctAnswerKey ?? null,
      student_answer: data.studentAnswerKey ?? null,
      is_Bookmarked: data.bookmarked,
      questionImageUrls: question.remainingImageUrls,
      explanationImageUrls: explanation.remainingImageUrls,
      reference: data.reference?.trim() || null,
      options: data.options.map((option) => ({
        key: option.key,
        text: option.text,
      })),
    };
  }

  createBookmark(examId: string, questionId: string): Observable<CreateBookmarkResponse> {
    const payload: CreateBookmarkPayload = {
      examId,
      questionId,
    };

    return this.http.post<CreateBookmarkResponse>(
      `${this.apiUrl}/${BookmarkApiEndpoints.CREATE_BOOK_MARK}`,
      payload,
    );
  }

  removeBookmark(bookmarkId: string): Observable<RemoveBookmarkResponse> {
    return this.http.delete<RemoveBookmarkResponse>(
      `${this.apiUrl}/${BookmarkApiEndpoints.REMOVE_BOOK_MARK}/${bookmarkId}`,
    );
  }

  toggleBookmark(contentId: string, bookmark: boolean): Observable<TrackEventResponse> {
    const payload: TrackEventPayload = {
      content_id: contentId,
      content_type: 'MCQ',
      event_type: 'BOOKMARK',
      event_action: bookmark,
    };

    return this.http.post<TrackEventResponse>(
      `${this.apiUrl}/${BookmarkApiEndpoints.TRACK_EVENT}`,
      payload,
    );
  }
}
